import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../../components/common';
import { usePromptViewModel } from '../../viewmodels';
import { Prompt } from '../../models';

const PromptListPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    prompts,
    isLoading,
    createPrompt,
    isCreating,
    deletePrompt,
    isDeleting,
    setDefault,
    isSettingDefault,
  } = usePromptViewModel();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    template: '',
    variables: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!createForm.name) {
      setError('프롬프트명을 입력하세요.');
      return;
    }
    if (!createForm.template) {
      setError('프롬프트 템플릿을 입력하세요.');
      return;
    }

    try {
      // 템플릿에서 변수 추출 ({{변수명}} 형식)
      const variableMatches = createForm.template.match(/\{(\w+)\}/g) || [];
      const variables = [...new Set(variableMatches.map((v) => v.replace(/[{}]/g, '')))];

      await createPrompt({
        name: createForm.name,
        description: createForm.description,
        template: createForm.template,
        variables,
        is_active: true,
      });
      setCreateDialogOpen(false);
      setCreateForm({ name: '', description: '', template: '', variables: [] });
      setError(null);
    } catch {
      setError('프롬프트 생성에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!selectedPrompt) return;
    try {
      await deletePrompt(selectedPrompt.id);
      setDeleteDialogOpen(false);
      setSelectedPrompt(null);
    } catch {
      console.error('Failed to delete prompt');
    }
  };

  const handleSetDefault = async (prompt: Prompt) => {
    try {
      await setDefault(prompt.id);
    } catch {
      console.error('Failed to set default');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title="프롬프트 관리"
        breadcrumbs={[{ label: '프롬프트' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            새 프롬프트
          </Button>
        }
      />

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40}>기본</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>설명</TableCell>
                  <TableCell>변수</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>공유</TableCell>
                  <TableCell>등록일</TableCell>
                  <TableCell width={120}>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prompts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        등록된 프롬프트가 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  prompts.map((prompt) => (
                    <TableRow key={prompt.id} hover>
                      <TableCell>
                        <Tooltip title={prompt.is_default ? '기본 프롬프트' : '기본으로 설정'}>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefault(prompt)}
                            disabled={isSettingDefault || prompt.is_default}
                          >
                            {prompt.is_default ? (
                              <StarIcon color="warning" />
                            ) : (
                              <StarBorderIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>{prompt.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {prompt.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {prompt.variables.slice(0, 3).map((v) => (
                            <Chip key={v} label={v} size="small" variant="outlined" />
                          ))}
                          {prompt.variables.length > 3 && (
                            <Chip label={`+${prompt.variables.length - 3}`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prompt.is_active ? '활성' : '비활성'}
                          size="small"
                          color={prompt.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={prompt.is_shared ? '모든 사용자가 사용 가능' : '본인만 사용 가능'}>
                          <Chip
                            icon={prompt.is_shared ? <PublicIcon /> : <LockIcon />}
                            label={prompt.is_shared ? '공유' : '비공개'}
                            size="small"
                            color={prompt.is_shared ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {new Date(prompt.created_at).toLocaleDateString('ko-KR')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/prompts/${prompt.id}/edit`)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedPrompt(prompt);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={prompt.is_default}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 프롬프트 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>새 프롬프트</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="프롬프트명"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="설명"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="프롬프트 템플릿"
            value={createForm.template}
            onChange={(e) => setCreateForm({ ...createForm, template: e.target.value })}
            margin="normal"
            multiline
            rows={10}
            required
            placeholder={`상담 내용을 분석하여 JSON 형식으로 추출해주세요.

## 상담 내용:
{content}

## 추출 형식:
{
  "client_name": "수급자 이름",
  "consultation_date": "YYYY-MM-DD",
  "counseling_details": "상담 내용 요약"
}

반드시 JSON 형식으로만 응답하세요.`}
            helperText="변수는 {변수명} 형식으로 작성합니다. 예: {content}, {client_name}"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>취소</Button>
          <Button onClick={handleCreate} variant="contained" disabled={isCreating}>
            {isCreating ? '생성 중...' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>프롬프트 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedPrompt?.name}" 프롬프트를 삭제하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptListPage;
